import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  FARB_VORSCHLAEGE,
  MARKEN_STILE,
  baueMarkenset,
  defaultBrandKit,
  farbenAusBild,
  type MarkenStil,
} from '@schnitt/shared';
import { api } from '../api';
import { useEditor } from '../store';
import { Sheet } from './Sheet';
import { TemplatePreview } from './BrandKitSheet';

/**
 * **Der Markenset-Assistent** (7.167).
 *
 * Sascha, 24. September: „baue mir einen marken kit builder. Weil ich kann so
 * schlecht was basteln und einstellen“. Vier Schritte, in jedem nur Auswahl:
 * Logo (oder keins), Farben (aus dem Logo gelesen oder ein fertiges Paar),
 * Stil (drei fertige, jeder mit Vorschau), Name. Am Ende ein neues Markenset,
 * das gleich für das Projekt gilt. Feineinstellen geht danach wie bisher im
 * Blatt „Markenset“ -- muss aber niemand.
 *
 * Die Schrift fragt der Assistent nicht: die Vorschau zeigt die Schrift der
 * Seite, der Export die eigene Datei. Eine Wahl hier würde etwas zeigen, was
 * der Film nicht hat. Wer eine eigene Schrift will, lädt sie im Markenset
 * hoch.
 */
const SCHRITTE = ['Logo', 'Farben', 'Stil', 'Name'] as const;

type Farben = { primary: string; secondary: string };

export function MarkensetAssistent({ onClose, onFertig }: { onClose: () => void; onFertig: () => void }): ReactElement {
  const project = useEditor((s) => s.project);
  const toast = useEditor((s) => s.toast);
  const refreshProject = useEditor((s) => s.refreshProject);
  const [schritt, setSchritt] = useState(0);
  const [logo, setLogo] = useState<{ datei: File; url: string } | null>(null);
  const [ausLogo, setAusLogo] = useState<Farben | null>(null);
  const [farben, setFarben] = useState<Farben>(FARB_VORSCHLAEGE[0]!);
  const [stil, setStil] = useState<MarkenStil>('klar');
  const [name, setName] = useState('Meine Marke');
  const [busy, setBusy] = useState(false);
  const eingabe = useRef<HTMLInputElement>(null);

  useEffect(() => () => {
    if (logo) URL.revokeObjectURL(logo.url);
  }, [logo]);

  const kit = useMemo(() => baueMarkenset(defaultBrandKit('vorschau'), { ...farben, stil }), [farben, stil]);
  const vorlage = (id: string) => kit.templates.find((t) => t.id === id)!;

  /** Das Logo klein malen und seine Farben lesen. */
  const nimmLogo = (datei: File | undefined) => {
    if (!datei) return;
    const url = URL.createObjectURL(datei);
    setLogo({ datei, url });
    const bild = new Image();
    bild.onload = () => {
      const seite = 64;
      const flaeche = document.createElement('canvas');
      flaeche.width = seite;
      flaeche.height = seite;
      const ctx = flaeche.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(bild, 0, 0, seite, seite);
      const gelesen = farbenAusBild(ctx.getImageData(0, 0, seite, seite).data);
      setAusLogo(gelesen);
      if (gelesen) setFarben(gelesen);
      setSchritt(1);
    };
    bild.onerror = () => toast('error', 'Das Bild liess sich nicht lesen. Ein PNG oder JPG geht.');
    bild.src = url;
  };

  const fertig = async () => {
    if (!project) return;
    setBusy(true);
    try {
      const sauber = name.trim().slice(0, 80) || 'Meine Marke';
      const { brandKit: angelegt } = await api.createBrandKit(sauber);
      await api.saveBrandKit(angelegt.id, sauber, baueMarkenset(angelegt.data, { ...farben, stil }));
      if (logo) await api.uploadBrandFile(angelegt.id, 'logo', logo.datei);
      await api.setProjectBrandKit(project.id, angelegt.id);
      await refreshProject();
      toast('success', `„${sauber}“ ist angelegt und gilt jetzt für dieses Projekt.`);
      onFertig();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Das Markenset liess sich nicht anlegen.');
    } finally {
      setBusy(false);
    }
  };

  const farbPaar = (f: Farben, beschriftung: string, key: string) => (
    <button
      key={key}
      className={`chip${farben.primary === f.primary && farben.secondary === f.secondary ? ' active' : ''}`}
      data-assistent-farbe={key}
      onClick={() => setFarben({ primary: f.primary, secondary: f.secondary })}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      <span style={{ width: 14, height: 14, borderRadius: 7, background: f.primary, display: 'inline-block' }} />
      <span style={{ width: 14, height: 14, borderRadius: 7, background: f.secondary, display: 'inline-block', border: '1px solid #ffffff33' }} />
      {beschriftung}
    </button>
  );

  return (
    <Sheet title="Markenset-Assistent" onClose={onClose} closeLabel="Abbrechen">
      <p className="hint" style={{ marginTop: 0 }} data-assistent-schritt={SCHRITTE[schritt]}>
        Schritt {schritt + 1} von {SCHRITTE.length}: {SCHRITTE[schritt]}
      </p>

      {schritt === 0 ? (
        <div>
          <p>Hast du ein Logo? Dann liest der Assistent die Farben daraus.</p>
          <input ref={eingabe} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={(e) => nimmLogo(e.target.files?.[0])} data-assistent-logo-datei />
          <button className="primary" style={{ width: '100%' }} onClick={() => eingabe.current?.click()} data-assistent-logo>
            Logo auswählen
          </button>
          <button style={{ width: '100%', marginTop: 8 }} onClick={() => setSchritt(1)} data-assistent-ohne-logo>
            Ohne Logo weiter
          </button>
        </div>
      ) : null}

      {schritt === 1 ? (
        <div>
          {logo ? (
            <div style={{ textAlign: 'center', marginBottom: 10 }}>
              <img src={logo.url} alt="" style={{ maxWidth: 120, maxHeight: 80 }} />
            </div>
          ) : null}
          <p>Welche Farben?</p>
          <div className="chips">
            {ausLogo ? farbPaar(ausLogo, 'Aus deinem Logo', 'logo') : null}
            {FARB_VORSCHLAEGE.map((f) => farbPaar(f, f.name, f.name))}
          </div>
          {logo && !ausLogo ? <p className="hint">Im Logo steckt keine Farbe -- nimm ein Paar, das dazu passt.</p> : null}
          <div style={{ marginTop: 12 }}>
            <TemplatePreview kit={kit} template={vorlage('lower-third')} logoUrl={logo?.url ?? null} />
          </div>
        </div>
      ) : null}

      {schritt === 2 ? (
        <div>
          <p>Welcher Stil?</p>
          <div style={{ display: 'grid', gap: 12 }}>
            {MARKEN_STILE.map((s) => {
              const probe = baueMarkenset(defaultBrandKit('probe'), { ...farben, stil: s.id });
              return (
                <button
                  key={s.id}
                  className={`stil-karte${stil === s.id ? ' active' : ''}`}
                  data-assistent-stil={s.id}
                  onClick={() => setStil(s.id)}
                  style={{
                    textAlign: 'left',
                    padding: 8,
                    borderRadius: 12,
                    border: stil === s.id ? '2px solid var(--accent, #4aa3ff)' : '2px solid transparent',
                    background: 'var(--card, #ffffff0d)',
                  }}
                >
                  <strong>{s.name}</strong> <span className="sub">{s.satz}</span>
                  <TemplatePreview kit={probe} template={probe.templates.find((t) => t.id === 'lower-third')!} logoUrl={logo?.url ?? null} ohneSatz />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {schritt === 3 ? (
        <div>
          <label className="field">
            <span>Wie heisst das Markenset?</span>
            <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-assistent-name />
          </label>
          <p className="hint">So sieht es aus:</p>
          <TemplatePreview kit={kit} template={vorlage('lower-third')} logoUrl={logo?.url ?? null} ohneSatz />
          <div style={{ marginTop: 8 }}>
            <TemplatePreview kit={kit} template={vorlage('title-card')} logoUrl={logo?.url ?? null} ohneSatz />
          </div>
        </div>
      ) : null}

      <div className="row" style={{ marginTop: 16, gap: 8 }}>
        {schritt > 0 ? (
          <button onClick={() => setSchritt(schritt - 1)} disabled={busy} data-assistent-zurueck>
            Zurück
          </button>
        ) : null}
        <span className="grow" />
        {schritt > 0 && schritt < SCHRITTE.length - 1 ? (
          <button className="primary" onClick={() => setSchritt(schritt + 1)} data-assistent-weiter>
            Weiter
          </button>
        ) : null}
        {schritt === SCHRITTE.length - 1 ? (
          <button className="primary" onClick={() => void fertig()} disabled={busy || !project} data-assistent-fertig>
            {busy ? '…' : 'Anlegen und verwenden'}
          </button>
        ) : null}
      </div>
    </Sheet>
  );
}
